import React, { useState, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  Modal,
  Text,
  Caption,
  tokens,
} from '../design-system';
import { useCreatePlanFromIdea } from '../hooks/api/useIdeas';
import { useApp } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

const CreatePlanModal = ({ isOpen, onClose, idea, onSuccess }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();
  const [planName, setPlanName] = useState('');
  const [selectedNames, setSelectedNames] = useState(null); // null = not yet initialised

  const createPlanMutation = useCreatePlanFromIdea();
  const posthog = usePostHog();

  // Only plannable features (exclude shipped — those are already in the codebase)
  const plannableFeatures = useMemo(() =>
    (idea?.idea_spec?.core_features || []).filter(f => f.status !== 'shipped'),
    [idea?.idea_spec?.core_features]
  );
  const hasFeatures = plannableFeatures.length > 0;
  const hasConnectedRepos = idea?.github_repos?.length > 0;

  // Default: all features selected when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setPlanName('');
      setSelectedNames(plannableFeatures.map(f => f.name));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = selectedNames?.length === plannableFeatures.length;
  const noneSelected = !selectedNames?.length;

  const toggleFeature = (name) => {
    setSelectedNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = planName.trim() || 'New Plan';
    const chosenNames = selectedNames ?? plannableFeatures.map(f => f.name);
    const allChosen = chosenNames.length === plannableFeatures.length;

    try {
      const result = await createPlanMutation.mutateAsync({
        ideaId: idea.id,
        planData: {
          name,
          scope: hasFeatures ? (allChosen ? 'full' : 'custom') : 'full',
          selected_features: hasFeatures ? chosenNames : undefined,
        },
      });

      posthog?.capture('plan_created', {
        scope: hasFeatures ? (allChosen ? 'full' : 'custom') : 'full',
        feature_count: chosenNames.length,
        has_github_repos: hasConnectedRepos,
      });

      showSuccess(`Plan "${name}" created`);
      onSuccess?.(result);

      if (result?.plan_id) navigate(`/plans/${result.plan_id}`);
      else if (result?.id) navigate(`/plans/${result.id}`);
    } catch (error) {
      showError(error.message || 'Failed to create plan');
    }
  };

  const isDisabled = createPlanMutation.isPending || (hasFeatures && noneSelected);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={null} size="lg" style={{
      backgroundColor: 'rgba(46,33,9,0.35)',
    }}>
      <div style={{
        backgroundColor: T.card,
        borderRadius: T.r4,
        boxShadow: T.sh2,
        fontFamily: T.sans,
        overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          padding: '18px 24px 16px',
          borderBottom: `1px solid ${T.rule}`,
          backgroundColor: T.paperSoft,
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '17px',
            fontWeight: 700,
            color: T.ink,
            fontFamily: T.sans,
            letterSpacing: '-0.01em',
          }}>
            Create Plan
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
          {/* Plan Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '13px',
              fontWeight: 600,
              color: T.ink2,
              fontFamily: T.sans,
            }}>
              Plan Name
            </label>
            <input
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="New Plan"
              autoFocus
              style={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: T.card,
                border: `1px solid ${T.rule}`,
                borderRadius: T.r2,
                padding: '8px 12px',
                fontSize: '14px',
                color: T.ink,
                fontFamily: T.sans,
                outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = T.ruleStrong; e.target.style.boxShadow = `0 0 0 2px ${T.rule}40`; }}
              onBlur={e => { e.target.style.borderColor = T.rule; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          {/* Connected repos indicator */}
          {hasConnectedRepos && (
            <div style={{
              padding: '10px 14px',
              backgroundColor: `${T.moss}12`,
              border: `1px dashed ${T.moss}60`,
              borderRadius: T.r3,
              marginBottom: '20px',
            }}>
              <span style={{
                fontSize: '12px',
                color: T.moss,
                fontWeight: 500,
                fontFamily: T.sans,
              }}>
                {idea.github_repos.length} repo{idea.github_repos.length > 1 ? 's' : ''} connected
                {' — '}
                {idea.github_repos.map(r => `${r.owner}/${r.repo}`).join(', ')}
              </span>
            </div>
          )}

          {/* Feature list */}
          {hasFeatures ? (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px',
              }}>
                <label style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: T.ink2,
                  fontFamily: T.sans,
                }}>
                  Features
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedNames(plannableFeatures.map(f => f.name))}
                    disabled={allSelected}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: allSelected ? 'default' : 'pointer',
                      color: allSelected ? T.inkFaint : T.orange,
                      fontSize: '12px',
                      padding: 0,
                      fontFamily: T.sans,
                      fontWeight: 500,
                      opacity: allSelected ? 0.5 : 1,
                    }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedNames([])}
                    disabled={noneSelected}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: noneSelected ? 'default' : 'pointer',
                      color: noneSelected ? T.inkFaint : T.inkMuted,
                      fontSize: '12px',
                      padding: 0,
                      fontFamily: T.sans,
                      fontWeight: 500,
                      opacity: noneSelected ? 0.5 : 1,
                    }}
                  >
                    Select none
                  </button>
                </div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                maxHeight: '280px',
                overflowY: 'auto',
                border: `1px solid ${T.rule}`,
                borderRadius: T.r3,
                padding: '8px',
                backgroundColor: T.paperSoft,
              }}>
                {plannableFeatures.map((feature) => {
                  const isSelected = (selectedNames ?? []).includes(feature.name);
                  return (
                    <label
                      key={feature.name}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        padding: '8px 10px',
                        borderRadius: T.r2,
                        cursor: 'pointer',
                        backgroundColor: isSelected ? `${T.orange}14` : 'transparent',
                        border: isSelected ? `1px solid ${T.orange}40` : '1px solid transparent',
                        transition: 'background-color 0.12s, border-color 0.12s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFeature(feature.name)}
                        style={{
                          marginTop: '2px',
                          accentColor: T.orange,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: T.ink,
                          fontFamily: T.sans,
                          margin: 0,
                        }}>
                          {feature.name}
                        </div>
                        {feature.description && (
                          <div style={{
                            marginTop: '2px',
                            fontSize: '12px',
                            color: T.inkMuted,
                            lineHeight: 1.4,
                            fontFamily: T.sans,
                          }}>
                            {feature.description}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {noneSelected && (
                <div style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: T.capRed,
                  fontFamily: T.sans,
                }}>
                  Select at least one feature
                </div>
              )}
              {!noneSelected && (
                <div style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: T.inkFaint,
                  fontFamily: T.sans,
                }}>
                  {selectedNames?.length} of {plannableFeatures.length} features selected
                </div>
              )}
            </div>
          ) : (
            idea?.idea_spec && (
              <div style={{
                padding: '10px 14px',
                backgroundColor: T.paperDeep,
                border: `1px dashed ${T.rule}`,
                borderRadius: T.r3,
                marginBottom: '20px',
              }}>
                <span style={{ fontSize: '13px', color: T.inkMuted, fontFamily: T.sans }}>
                  No plannable features in spec — plan will cover the full idea.
                </span>
              </div>
            )
          )}

          {/* Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '18px',
            borderTop: `1px solid ${T.rule}`,
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={createPlanMutation.isPending}
              style={{
                padding: '8px 18px',
                backgroundColor: 'transparent',
                border: `1px dashed ${T.rule}`,
                borderRadius: T.r3,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: T.inkMuted,
                fontFamily: T.sans,
                opacity: createPlanMutation.isPending ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isDisabled}
              style={{
                padding: '8px 20px',
                backgroundColor: isDisabled ? T.paperDeep : T.orange,
                border: 'none',
                borderRadius: T.r3,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                color: isDisabled ? T.inkFaint : '#fff',
                fontFamily: T.sans,
                transition: 'background-color 0.12s',
                boxShadow: isDisabled ? 'none' : T.sh1,
              }}
              onMouseEnter={e => { if (!isDisabled) e.target.style.backgroundColor = T.orangeDark; }}
              onMouseLeave={e => { if (!isDisabled) e.target.style.backgroundColor = T.orange; }}
            >
              {createPlanMutation.isPending ? 'Creating...' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreatePlanModal;
